/**
 * Collapsible Section Component
 * Generic wrapper for sections that can be collapsed/expanded
 * Persists state to localStorage
 */

import { useState, useEffect, type ReactNode } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'

interface CollapsibleSectionProps {
  /** Unique identifier for localStorage persistence */
  id: string
  /** Section title */
  title: string
  /** Optional badge to show next to title (e.g., count) */
  badge?: ReactNode
  /** Whether section is open by default */
  defaultOpen?: boolean
  /** Section content */
  children: ReactNode
  /** Optional className for the container */
  className?: string
}

const STORAGE_PREFIX = 'dashboard.section.'

export function CollapsibleSection({
  id,
  title,
  badge,
  defaultOpen = true,
  children,
  className = '',
}: CollapsibleSectionProps): React.ReactElement {
  const [isOpen, setIsOpen] = useState(() => {
    // Try to load from localStorage
    const stored = localStorage.getItem(`${STORAGE_PREFIX}${id}.collapsed`)
    if (stored !== null) {
      return stored !== 'true'
    }
    return defaultOpen
  })

  useEffect(() => {
    // Persist to localStorage
    localStorage.setItem(`${STORAGE_PREFIX}${id}.collapsed`, String(!isOpen))
  }, [id, isOpen])

  const toggle = (): void => {
    setIsOpen(!isOpen)
  }

  return (
    <div className={className}>
      <button
        onClick={toggle}
        className="flex w-full items-center justify-between py-2 text-left"
        aria-expanded={isOpen}
        aria-controls={`section-${id}`}
      >
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--text)' }}>
            {title}
          </h2>
          {badge}
        </div>
        <div
          className="cursor-pointer rounded-lg p-1 transition-colors"
          style={{ color: 'var(--text-muted)' }}
        >
          {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </div>
      </button>

      <div
        id={`section-${id}`}
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="pt-2">{children}</div>
      </div>
    </div>
  )
}
